import React, { useState, useEffect } from "react";
import "../../Assets/Styles/pages/maintenance.scss";
import usePageMetadata from "../../Utils/usePageMetadata";
import logo from "../../Assets/Images/landing/eww-logo.png";

const TOTAL_COUNTDOWN = 30;

const Maintenance: React.FC = () => {
  usePageMetadata(
    "Under Maintenance - eZy Watermark",
    "eZy Watermark is currently undergoing scheduled maintenance. We will be back online shortly."
  );

  const [countdown, setCountdown] = useState(TOTAL_COUNTDOWN);
  const [checking, setChecking] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  const checkServer = async () => {
    setChecking(true);
    setStatusMessage(null);
    try {
      const res = await fetch(`/?check=${Date.now()}`, { method: "HEAD", cache: "no-store" });
      if (res.ok) {
        setStatusMessage({ text: "Server is back online! Redirecting to home...", type: "success" });
        setTimeout(() => {
          window.location.href = "/";
        }, 800);
      } else {
        throw new Error("Server not ready");
      }
    } catch {
      setStatusMessage({
        text: "Maintenance is still underway. Will recheck automatically.",
        type: "error",
      });
      setCountdown(TOTAL_COUNTDOWN);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          void checkServer();
          return TOTAL_COUNTDOWN;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const progressPercent = Math.max(0, (countdown / TOTAL_COUNTDOWN) * 100);

  return (
    <div className="maintenance-page">
      <div className="ambient-glow glow-1" />
      <div className="ambient-glow glow-2" />

      <div className="maintenance-page__card">
        {/* Brand */}
        <div className="maintenance-page__brand">
          <div className="maintenance-page__brand-icon">
            <img src={logo} alt="eZy Watermark" />
          </div>
          <span className="maintenance-page__brand-name">
            eZy <span>Watermark</span>
          </span>
        </div>

        {/* Illustration */}
        <div className="maintenance-page__illustration">
          <div className="maintenance-page__illustration-bg" />
          <svg
            className="gear-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f25665"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </div>

        {/* Status Badge */}
        <div className="maintenance-page__badge">
          <span className="dot" />
          <span>System Maintenance in Progress</span>
        </div>

        {/* Title and Description */}
        <h1 className="maintenance-page__title">We’ll be right back!</h1>
        <p className="maintenance-page__desc">
          We are currently performing scheduled maintenance to improve your experience.
          All your watermark templates, account data, and processed images are safe.
        </p>

        {/* Auto-refresh timer */}
        <div className="maintenance-page__timer">
          <div className="maintenance-page__timer-info">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>
              Auto-checking in <strong>{countdown}s</strong>
            </span>
          </div>
          <div className="maintenance-page__timer-bar">
            <div
              className="maintenance-page__timer-bar-progress"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="maintenance-page__actions">
          <button
            type="button"
            className="btn-action btn-action--primary"
            onClick={() => void checkServer()}
            disabled={checking}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={checking ? { animation: "maintenanceRotate 1s linear infinite" } : undefined}
            >
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {checking ? "Checking..." : "Check Again"}
          </button>

          <a
            href="mailto:support@whizpool.com?subject=eZy%20Watermark%20Maintenance%20Status"
            className="btn-action btn-action--secondary"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Contact Support
          </a>
        </div>

        {/* Live Status Message */}
        {statusMessage && (
          <div className={`maintenance-page__status-msg ${statusMessage.type}`}>
            {statusMessage.text}
          </div>
        )}

        {/* Footer info */}
        <div className="maintenance-page__footer">
          <span>Status: Scheduled Maintenance (503 / 500)</span>
          <span>{new Date().toUTCString()}</span>
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
